import { getSerializedMetadata } from './serialized.decorator';

export class SerializeUtil {
  public static normalizeJson(serialized: string | any): any {
    if (typeof serialized === 'string') {
      try {
        return JSON.parse(serialized);
      } catch (e) {
        console.error('Could not deserialize: invalid JSON. Error and JSON follow:', e, serialized);
        throw e;
      }
    } else {
      return serialized;
    }
  }

  /**
   * Deeply deserialize an object.
   * This deserializes the object, then deserializes any of its instance members that need special handling to deserialize.
   * @param instance A fresh instance to deserialize onto, which will be mutated
   * @param serialized The serialized data produced by a previous call to {@link deepSerialize}
   * @returns The deserialized object
   */
  public static deepDeserialize<T>(instance: T, serialized: string | object): T {
    const pojo = SerializeUtil.normalizeJson(serialized) as T;
    (Object.keys(pojo) as Array<keyof T>).forEach(propertyName => {
      const serializeMetadata = getSerializedMetadata(instance, propertyName as string);
      if (!serializeMetadata) { return; }

      const plainValue: any = pojo[propertyName];
      if (plainValue === null || typeof plainValue === 'undefined') {
        (instance[propertyName] as any) = plainValue;
      } else if (serializeMetadata.list) {
        (instance[propertyName] as any) = (plainValue as any[]).map(item => serializeMetadata.deserialize(item));
      } else {
        (instance[propertyName] as any) = serializeMetadata.deserialize(plainValue);
      }
    });
    return instance;
  }

  /**
   * Deeply serialize an object.
   * This serializes the object, as well as serializing any instance members that need special handling to serialize.
   * @param instance The instance to serialize
   * @returns The serialized instance
   */
  public static deepSerialize<T>(instance: T): string {
    const pojo: any = { };
    (Object.keys(instance) as Array<keyof T>).forEach(propertyName => {
      const serializeMetadata = getSerializedMetadata(instance, propertyName as string);
      if (!serializeMetadata) { return; }

      const value: any = instance[propertyName];
      if (value === null || typeof value === 'undefined') {
        (pojo[propertyName] as any) = value;
      } else if (serializeMetadata.list) {
        (pojo[propertyName] as any) = (value as any[]).map(item => serializeMetadata.serialize(item));
      } else {
        (pojo[propertyName] as any) = serializeMetadata.serialize(value);
      }
    });
    return JSON.stringify(pojo);
  }
}
